import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";

import { useAppDispatch, useAppSelector } from "../../redux";
import { InputField, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { AgentFunctionEditor } from "../../components/agents/AgentFunctionEditor";
import { featureActions, RecipeCreationFormData } from "../../redux/reducers/featureSlice";
import { Tabs, Tab } from "../../components/base/Tabs";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

export const AgentRecipeCreationPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();

  const agent = useAppSelector((state) => (agentId ? state.agent.entities[agentId] : null));
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));

  const agentEnvironmentIds = agentEnvironments.map((env) => env.id);

  const agentEnvironmentVariables = useAppSelector((state) =>
    Object.values(state.agentEnvironmentVariable.entities).filter((item) => agentEnvironmentIds.includes(item.agentEnvironmentId)),
  );
  const agentFunction = useAppSelector((state) => Object.values(state.agentFunction.entities).find((func) => func.agentId === agentId));
  const formData = useAppSelector((state) => state.feature.recipeCreationFormData);

  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    if (agent && agentFunction && !formData.agentId) {
      dispatch(
        featureActions.setRecipeCreationFormData({
          name: "",
          description: "",
          agentId: agent.id,
          language: agentFunction.language,
          defaultVoiceId: null,
        }),
      );
    } else if (agent && agentFunction && (formData.agentId !== agent.id || formData.language !== agentFunction.language)) {
      dispatch(
        featureActions.setRecipeCreationFormData({
          ...formData,
          agentId: agent.id,
          language: agentFunction.language,
        }),
      );
    }
  }, [agent, agentFunction, dispatch, formData]);

  const tabs: Tab[] = useMemo(() => {
    return agentEnvironments?.map((env) => ({ id: env.id, label: env.name })) ?? [];
  }, [agentEnvironments]);

  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
    if (activeTab && !tabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(tabs.length > 0 ? tabs[0].id : null);
    }
  }, [tabs, activeTab]);

  const activeEnvironment = useMemo(() => {
    return agentEnvironments?.find((env) => env.id === activeTab) ?? null;
  }, [agentEnvironments, activeTab]);

  const language = useMemo(() => {
    return agentFunction?.language || "python";
  }, [agentFunction]);

  const activeEnvVariables = useMemo(() => {
    if (!activeEnvironment) return [];
    return agentEnvironmentVariables.filter((var_) => var_.agentEnvironmentId === activeEnvironment.id);
  }, [activeEnvironment, agentEnvironmentVariables]);

  const handleFieldChange = <K extends keyof RecipeCreationFormData>(name: K, value: RecipeCreationFormData[K]) => {
    dispatch(featureActions.setRecipeCreationFormData({ ...formData, [name]: value }));
  };

  if (!agent || !agentFunction) {
    return (
      <View className="flex-1 justify-center items-center">
        <Text>Loading agent data...</Text>
      </View>
    );
  }

  const placeholderText =
    agentEnvironments && agentEnvironments.length > 0 ? "Select an environment tab to view details." : "No environments defined for this agent yet.";

  return (
    <View className="relative flex flex-row h-full overflow-hidden">
      <View className="w-2/3 overflow-y-auto bg-gray-50 dark:bg-gray-900/60 flex flex-col min-h-0">
        <motion.div
          className="flex flex-col flex-1 min-h-0"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <Tabs
            tabs={tabs}
            activeTab={activeTab || ""}
            onChange={setActiveTab}
            className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700 px-4"
          />

          <View className="flex flex-col flex-1 min-h-0 gap-4 p-4">
            {activeEnvironment ? (
              <>
                <View className="grid grid-cols-3 gap-4">
                  <View className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm col-span-2">
                    <Text className="text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">Required Variables</Text>
                    <View>
                      {activeEnvVariables && activeEnvVariables.length > 0 ? (
                        activeEnvVariables.map((variable) => (
                          <Text
                            key={variable.id}
                            className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mr-2 mb-2 inline-block"
                          >
                            {variable.name}
                          </Text>
                        ))
                      ) : (
                        <Text className="text-sm text-gray-400 dark:text-gray-500 italic">None required for {activeEnvironment.name}</Text>
                      )}
                    </View>
                  </View>

                  <View className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                    <Text className="text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">Language</Text>
                    <View className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 mt-1 py-2 rounded-md w-fit">
                      <Icon
                        name={language === "python" ? "python" : "javascript"}
                        size={16}
                      />
                      <Text className="capitalize text-gray-700 dark:text-gray-300">{language}</Text>
                    </View>
                  </View>
                </View>
              </>
            ) : (
              <View className={clsx("p-4", tabs.length === 0 ? "block" : "hidden md:block")}>
                <Text className="text-gray-500 dark:text-gray-400">{placeholderText}</Text>
              </View>
            )}

            <View className="pb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col flex-1 min-h-0">
              <Text className="mt-2 ml-4 text-base font-semibold mb-2 text-gray-700 dark:text-gray-300">Function Code</Text>
              <AgentFunctionEditor
                value={agentFunction.code}
                language={language}
                readOnly={true}
                onChange={() => {}}
              />
            </View>
          </View>
        </motion.div>
      </View>

      <View className="w-1/3 p-4 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
        <motion.div
          className="flex flex-col flex-1 gap-6"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <InputField
            name="name"
            label="Recipe Name"
            placeholder="Give your recipe a descriptive name"
            value={formData.name || ""}
            onChange={(value) => handleFieldChange("name", value)}
          />
          <InputField
            name="description"
            label="Recipe Description"
            placeholder="Describe what this recipe does..."
            type="textarea"
            value={formData.description || ""}
            onChange={(value) => handleFieldChange("description", value)}
            className="min-h-[150px]"
          />
        </motion.div>
      </View>
    </View>
  );
};
